import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { Permission, SubUser, AllowedAccounts } from '@/interfaces/user';
import subUserServices from '@/api/subUserService';
import { ItermediateAccount } from '@/interfaces/account';
import { Transaction } from '@/interfaces/transaction';
import { groupAndSortTransactions } from '@/utils/groupTransactions';
import transactionsServices from '@/api/transactionsServices';

interface SubUsersByFilter {
  [key: string]: {
    subUsers: SubUser[];
    meta: {
      totalPages: number;
      totalElements: number;
    };
  };
}

export interface SubUserFormData {
  fullName: string;
  phoneNumber: string;
  email: string;
  jobTitle: string;
  validatorsCount: string;
  allowedAccounts: AllowedAccounts[];
  isShowBalance: boolean;
  permissions: string[];
}

export const initialSubUserFormData: SubUserFormData = {
  fullName: '',
  phoneNumber: '',
  email: '',
  jobTitle: '',
  validatorsCount: '1',
  allowedAccounts: [{ accountId: '', currency: '', singleTransactionAmountLimit: '', name: '' }],
  isShowBalance: true,
  permissions: [],
};

interface TransactionsByAccounts {
  [key: string]: {
    [key: string]: Transaction[];
  };
}

interface SubUserState {
  subUsersByFilt: SubUsersByFilter | undefined;
  transByAcc: TransactionsByAccounts;
  transByDate: Record<string, Transaction[]>;
  numTransactions: number;
  selectedSubUser: SubUser;
  selectedAccId: string;
  loading: boolean;
  subTransLoading: boolean;
  numPages: number;
  error: string;
  emailForSuccess: string;
  subError: string;
  totalElements: number;
  totalPages: number;
  number: number;
  recordsSize: number;
  sort: string;
}

const initialSubUser = {
  id: '',
  firstName: '',
  email: '',
  jobTitle: '',
  lastName: '',
  username: '',
  allowedAccounts: [],
  permissions: [],
  isShowBalance: true,
};

const initialState: SubUserState = {
  subUsersByFilt: undefined,
  numTransactions: 0,
  numPages: 0,
  selectedSubUser: initialSubUser,
  transByAcc: {} as { string: { string: Transaction[] } },
  transByDate: {},
  selectedAccId: '',
  loading: false,
  subTransLoading: false,
  error: '',
  subError: '',
  emailForSuccess: '',
  totalElements: 0,
  totalPages: 0,
  number: 0,
  recordsSize: 0,
  sort: '',
};

export const fetchSubUsers = createAsyncThunk('subUsers/fetch', async (params: any) => {
  return subUserServices.loadSubUsers(params);
});

export const deleteSubUser = createAsyncThunk('subUsers/delete', async (id: string, thunkAPI) => {
  try {
    const result = await subUserServices.deleteSubUser(id);
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const createSubUser = createAsyncThunk('subUsers/create', async (user: SubUser, thunkAPI) => {
  try {
    const result = await subUserServices.create(user);
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.data);
  }
});

export const changeSubUserState = createAsyncThunk(
  'subUsers/changeState',
  async ({ id, enabled }: { id: string; enabled: boolean }, thunkAPI) => {
    try {
      const result = await subUserServices.changeState(id, enabled);
      return { id, enabled, result };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.data);
    }
  }
);

export const fetchSubUserTransactions = createAsyncThunk('subUsersTrans/fetch', async ({ filters = {} }: { filters?: any }) => {
  return transactionsServices.loadSubUserTransaction(filters);
});

export const subUsersSlice = createSlice({
  name: 'subUsers',
  initialState,
  reducers: {
    refresh: () => initialState,
    selectSubUser: (state, action: PayloadAction<SubUser>) => {
      state.selectedSubUser = action.payload;
    },
    initTransByDate: state => {
      state.transByDate = {};
      state.numTransactions = 0;
    },
    handleAccountChange: (state, action: PayloadAction<string>) => {
      state.selectedAccId = action.payload;
    },
    handleTransByDateChange: (state, action: PayloadAction<{ subUserId: string; accountId: string }>) => {
      const { subUserId, accountId } = action.payload;
      state.transByDate = groupAndSortTransactions(state.transByAcc[subUserId][accountId]);
      state.numTransactions = state.transByAcc[subUserId][accountId].length;
    },
    setEmail: (state, action) => {
      state.emailForSuccess = action.payload;
    },
    setSort: (state, action) => {
      state.sort = action.payload;
    },
    updateSubUser: (
      state,
      action: PayloadAction<{ user: SubUser; fullPermissions: Permission[]; visibleAccounts: ItermediateAccount[] }>
    ) => {
      const { user, fullPermissions, visibleAccounts } = action.payload;
      if (!state.subUsersByFilt) {
        return;
      }
      Object.keys(state.subUsersByFilt).forEach(key => {
        state.subUsersByFilt![key].subUsers = state.subUsersByFilt![key].subUsers.map((subUser: SubUser) => {
          if (subUser.id === user.id) {
            return {
              ...user,
              permissions: user.permissions.map(
                (permission: string) => fullPermissions.find((el: Permission) => el.id === permission)!.name
              ),
              allowedAccounts: user.allowedAccounts.map((account: AllowedAccounts) => ({
                ...account,
                name: visibleAccounts.find(acc => acc.accountId === account.accountId)!.name,
              })),
            };
          }
          return subUser;
        });
      });
    },
  },

  extraReducers: builder => {
    builder.addCase(fetchSubUsers.pending, state => {
      state.loading = true;
    });

    builder.addCase(fetchSubUsers.fulfilled, (state, action) => {
      const {
        payload: { subUsers },
      } = action;

      if (!state.subUsersByFilt) {
        state.subUsersByFilt = {};
      }
      state.subUsersByFilt[state.sort] = {
        subUsers: subUsers.data,
        meta: { totalElements: subUsers.totalElements, totalPages: subUsers.totalPages },
      };
      state.loading = false;
    });

    builder.addCase(fetchSubUsers.rejected, state => {
      state.loading = false;
      state.error = 'Error';
    });

    builder.addCase(fetchSubUserTransactions.pending, state => {
      state.subTransLoading = true;
    });

    builder.addCase(fetchSubUserTransactions.fulfilled, (state, action) => {
      const {
        payload: { data, numberOfElements, totalPages, totalElements },
      } = action;
      if (!state.transByAcc[state.selectedSubUser.id!]) {
        state.transByAcc[state.selectedSubUser.id!] = {};
      }
      state.transByAcc[state.selectedSubUser.id!][state.selectedAccId] = data;
      state.transByDate = groupAndSortTransactions(data);
      state.numTransactions = numberOfElements;
      state.totalPages = totalPages;
      state.totalElements = totalElements;
      state.numPages = totalPages;
      state.subTransLoading = false;
    });

    builder.addCase(fetchSubUserTransactions.rejected, state => {
      state.subTransLoading = false;
      state.subError = 'Error';
    });

    builder.addCase(deleteSubUser.fulfilled, (state, action) => {
      const { meta } = action;
      if (!state.subUsersByFilt) {
        return;
      }
      const items = state.subUsersByFilt;
      const sort = state.sort;
      items[sort].subUsers = items[sort].subUsers.filter((subUser: SubUser) => subUser.id !== meta.arg);
      delete state.transByAcc[meta.requestId];
    });

    builder.addCase(changeSubUserState.fulfilled, (state, action) => {
      const { id, enabled } = action.payload;
      if (!state.subUsersByFilt) {
        return;
      }
      Object.keys(state.subUsersByFilt).forEach(key => {
        state.subUsersByFilt![key].subUsers = state.subUsersByFilt![key].subUsers.map((subUser: SubUser) => {
          if (subUser.id === id) {
            return { ...subUser, enabled, status: enabled ? 'ACTIVE' : 'INACTIVE' };
          }
          return subUser;
        });
      });
    });
  },
});

export const { refresh, initTransByDate, selectSubUser, handleAccountChange, handleTransByDateChange, setEmail, updateSubUser, setSort } =
  subUsersSlice.actions;

export const selectSubUsers = (state: RootState) => state.subUsers;

export default subUsersSlice.reducer;
